import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { DollarSign, Gift } from 'lucide-react';
import { SequenceFormData } from '../types';

interface PricingStepProps {
  form: UseFormReturn<SequenceFormData>;
  onNext: () => void;
  onBack: () => void;
}

const PricingStep: React.FC<PricingStepProps> = ({ form, onNext, onBack }) => {
  const isFree = form.watch('is_free');

  const handleNext = async () => {
    const isValid = await form.trigger(['is_free', 'price']);
    if (isValid) {
      onNext();
    }
  };

  const calculatePlatformFee = (price: number): number => {
    return price * 0.10; // 10% platform fee
  };

  const calculateYourEarnings = (price: number): number => {
    return price - calculatePlatformFee(price);
  };

  const price = form.watch('price') || 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          Set Your Price
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                Choose whether to offer your sequence for free or set a price. 
                Free sequences get more downloads and help build your reputation.
              </p>
            </div>

            <FormField
              control={form.control}
              name="is_free"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 p-4 border rounded-lg">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="flex items-center gap-2 text-base">
                      <Gift className="h-4 w-4 text-green-600" />
                      Offer this sequence for free
                    </FormLabel>
                    <p className="text-sm text-gray-600">
                      Make your sequence available to the community at no cost
                    </p>
                  </div>
                </FormItem>
              )}
            />

            {!isFree && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="price"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Price (USD) *</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                          <Input
                            {...field}
                            type="number"
                            min="0"
                            step="0.01"
                            className="pl-10"
                            value={field.value || ''}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                            placeholder="0.00"
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {price > 0 && (
                  <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                    <h4 className="font-medium text-sm">Pricing Breakdown</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sale Price:</span>
                        <span className="font-medium">${price.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Platform Fee (10%):</span>
                        <span className="text-red-600">-${calculatePlatformFee(price).toFixed(2)}</span>
                      </div>
                      <div className="border-t pt-2 flex justify-between">
                        <span className="font-medium">Your Earnings:</span>
                        <span className="font-medium text-green-600">
                          ${calculateYourEarnings(price).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-sm text-yellow-900 mb-2">Pricing Tips</h4>
                  <ul className="space-y-1 text-sm text-yellow-800">
                    <li>• Most sequences sell for $5-20</li>
                    <li>• Complex sequences with many props can command higher prices</li>
                    <li>• Consider starting with a lower price to build reviews</li>
                    <li>• You can always adjust your price later</li>
                  </ul>
                </div>
              </div>
            )}

            <div className="flex justify-between">
              <Button type="button" variant="outline" onClick={onBack}>
                Back
              </Button>
              <Button type="button" onClick={handleNext}>
                Next: Review & Submit
              </Button>
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
};

export default PricingStep;